import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Alert,
    useTheme
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Refresh as RefreshIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon,
    Description as DocumentIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { Media, mediaApi, MediaLimitInfo } from '../../utils/mediaApi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const MediaManagement: React.FC = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [medias, setMedias] = useState<Media[]>([]);
    const [loading, setLoading] = useState(false);
    const [limitInfo, setLimitInfo] = useState<MediaLimitInfo | null>(null);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
    const [filterText, setFilterText] = useState('');
    const [showOrphansOnly, setShowOrphansOnly] = useState(false);

    const loadMedias = async () => {
        setLoading(true);
        try {
            const [mediaList, limitData] = await Promise.all([
                mediaApi.getAll(),
                mediaApi.getMediaLimitInfo()
            ]);
            setMedias(mediaList);
            setLimitInfo(limitData);
        } catch (error) {
            console.error('Erro ao carregar mídias:', error);
            toast.error(t('media.management.errors.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMedias();
    }, []);

    const handleDelete = async (media: Media) => {
        if (!window.confirm(t('media.management.deleteConfirm', { fileName: media.fileName }))) {
            return;
        }

        try {
            await mediaApi.deleteMedia(media.id);
            toast.success(t('media.management.notifications.deleted'));
            await loadMedias();
        } catch (error) {
            console.error('Erro ao excluir mídia:', error);
            toast.error(t('media.management.errors.deleteError'));
        }
    };

    const handlePreview = (media: Media) => {
        setSelectedMedia(media);
        setPreviewDialog(true);
    };

    const handleCheckOrphans = async () => {
        try {
            const result = await mediaApi.checkOrphans();
            toast.success(result.message || t('media.management.notifications.orphansChecked'));
            await loadMedias();
        } catch (error) {
            console.error('Erro ao verificar mídias órfãs:', error);
            toast.error(t('media.management.errors.checkOrphansError'));
        }
    };

    const getFileIcon = (fileType: string) => {
        switch (fileType) {
            case 'image':
                return <ImageIcon color="primary" />;
            case 'video':
                return <VideoIcon color="secondary" />;
            case 'document':
                return <DocumentIcon color="action" />;
            default:
                return <DocumentIcon color="action" />;
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const locale = i18n.language === 'pt-BR' ? 'pt-BR' : i18n.language === 'es-ES' ? 'es-ES' : 'en-US';
        return date.toLocaleString(locale);
    };

    const filteredMedias = medias.filter(media => {
        const matchesFilter =
            media.fileName.toLowerCase().includes(filterText.toLowerCase()) ||
            media.agentName?.toLowerCase().includes(filterText.toLowerCase()) ||
            media.uploadedByName?.toLowerCase().includes(filterText.toLowerCase());

        const matchesOrphan = !showOrphansOnly || media.isOrphan;

        return matchesFilter && matchesOrphan;
    });

    const usagePercentage = limitInfo && !limitInfo.isUnlimited
        ? (limitInfo.currentCount / limitInfo.maxMedia) * 100
        : 0;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={700}>{t('media.management.title')}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadMedias}
                    >
                        {t('media.management.refresh')}
                    </Button>
                </Box>
            </Stack>

            {/* Informações de Limite */}
            {limitInfo && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>{t('media.management.planUsage')}</Typography>
                        <Box sx={{ mb: 1 }}>
                            <Stack direction="row" justifyContent="space-between" mb={1}>
                                <Typography variant="body2">
                                    {limitInfo.currentCount} / {limitInfo.isUnlimited ? '∞' : limitInfo.maxMedia} {t('media.management.medias')}
                                </Typography>
                                {!limitInfo.isUnlimited && (
                                    <Typography variant="body2" color={usagePercentage > 80 ? 'error' : 'text.secondary'}>
                                        {usagePercentage.toFixed(1)}%
                                    </Typography>
                                )}
                            </Stack>
                            {!limitInfo.isUnlimited && (
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(usagePercentage, 100)}
                                    color={usagePercentage > 80 ? 'error' : 'primary'}
                                />
                            )}
                        </Box>
                        {!limitInfo.canUpload && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {t('media.management.limitReached')}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Filtros */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            label={t('media.management.search')}
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder={t('media.management.searchPlaceholder')}
                        />
                        <Button
                            variant={showOrphansOnly ? 'contained' : 'outlined'}
                            onClick={() => setShowOrphansOnly(!showOrphansOnly)}
                            startIcon={<WarningIcon />}
                        >
                            {t('media.management.unusedOnly')}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Tabela de Mídias */}
            <Card>
                <CardContent>
                    {loading && <LinearProgress />}
                    <TableContainer sx={{ 
                        overflowX: { xs: 'auto', md: 'visible' },
                        '&::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: 'rgba(0,0,0,0.5)',
                        },
                    }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('media.management.table.type')}</TableCell>
                                    <TableCell>{t('media.management.table.file')}</TableCell>
                                    <TableCell>{t('media.management.table.agent')}</TableCell>
                                    <TableCell>{t('media.management.table.size')}</TableCell>
                                    <TableCell>{t('media.management.table.uploadedBy')}</TableCell>
                                    <TableCell>{t('media.management.table.date')}</TableCell>
                                    <TableCell>{t('media.management.table.status')}</TableCell>
                                    <TableCell align="right">{t('media.management.table.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredMedias.map((media) => (
                                    <TableRow key={media.id}>
                                        <TableCell>{getFileIcon(media.fileType)}</TableCell>
                                        <TableCell>
                                            <Tooltip title={media.fileName}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                    {media.fileName.split('/').pop()}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {media.agentName || <Typography color="text.secondary" variant="body2">-</Typography>}
                                        </TableCell>
                                        <TableCell>{formatFileSize(media.fileSize)}</TableCell>
                                        <TableCell>{media.uploadedByName || '-'}</TableCell>
                                        <TableCell>{formatDate(media.uploadedAt)}</TableCell>
                                        <TableCell>
                                            {media.isOrphan && (
                                                <Chip
                                                    label={t('media.management.unused')}
                                                    sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                                                    size="small"
                                                    color="warning"
                                                    icon={<WarningIcon />}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Tooltip title={t('media.management.view')}>
                                                    <IconButton onClick={() => handlePreview(media)} size="small">
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('media.management.delete')}>
                                                    <IconButton onClick={() => handleDelete(media)} size="small" color="error">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredMedias.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="text.secondary" py={2}>
                                                {filterText || showOrphansOnly ? t('media.management.noMediaFiltered') : t('media.management.noMedia')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Dialog de Preview */}
            <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{t('media.management.preview.title')}</DialogTitle>
                <DialogContent>
                    {selectedMedia && (
                        <Box>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('media.management.preview.file')}</Typography>
                                    <Typography variant="body1">{selectedMedia.fileName}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('media.management.preview.type')}</Typography>
                                    <Typography variant="body1">{selectedMedia.fileType}</Typography>
                                </Box>
                                {selectedMedia.agentName && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">{t('media.management.preview.agent')}</Typography>
                                        <Typography variant="body1">{selectedMedia.agentName}</Typography>
                                    </Box>
                                )}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('media.management.preview.preview')}</Typography>
                                    {selectedMedia.fileType === 'image' && (
                                        <Box sx={{ mt: 1 }}>
                                            <img
                                                src={selectedMedia.fileUrl}
                                                alt={selectedMedia.fileName}
                                                style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                            />
                                        </Box>
                                    )}
                                    {selectedMedia.fileType === 'video' && (
                                        <Box sx={{ mt: 1 }}>
                                            <video
                                                src={selectedMedia.fileUrl}
                                                controls
                                                style={{ maxWidth: '100%', maxHeight: '400px' }}
                                            />
                                        </Box>
                                    )}
                                    {selectedMedia.fileType === 'document' && (
                                        <Box sx={{ mt: 1 }}>
                                            <Button
                                                variant="contained"
                                                href={selectedMedia.fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('media.management.preview.openDocument')}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('media.management.preview.url')}</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {selectedMedia.fileUrl}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialog(false)}>{t('media.management.preview.close')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MediaManagement;
